import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Edit, Trash2, AlertCircle, CheckCircle, Search, Image as ImageIcon, Building } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import AdminGlobalLayout from '../../components/AdminGlobalLayout';
import AdminDataTable from '../../components/AdminDataTable';

interface Event {
  id: string;
  title: string;
  date: string;
  time: string;
  description: string | null;
  created_at: string;
  user_id: string;
  igreja_id: string;
  igreja_nome?: string;
  images?: EventImage[];
}

interface EventImage {
  id: string;
  event_id: string;
  image_url: string;
  created_at: string;
}

interface Igreja {
  id: string;
  nome: string;
  cidade: string;
  estado: string;
}

interface EventFormData {
  title: string;
  date: string;
  time: string;
  description: string;
  igreja_id: string;
  image_url: string;
}

export default function AdminEvents() {
  const [events, setEvents] = useState<Event[]>([]);
  const [igrejas, setIgrejas] = useState<Igreja[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    date: '',
    time: '',
    description: '',
    igreja_id: '',
    image_url: ''
  });
  
  // Table state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | null>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [selectedIgreja, setSelectedIgreja] = useState<string>('');

  useEffect(() => {
    fetchEvents();
    fetchIgrejas();
  }, []);

  useEffect(() => {
    // Filter and sort events
    let filtered = [...events];
    
    // Apply igreja filter
    if (selectedIgreja) {
      filtered = filtered.filter(event => event.igreja_id === selectedIgreja);
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(event => 
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (event.description?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        event.igreja_nome?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    if (sortField && sortOrder) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        if (sortField === 'igreja_nome') {
          aValue = a.igreja_nome || '';
          bValue = b.igreja_nome || '';
        } else {
          aValue = a[sortField as keyof Event];
          bValue = b[sortField as keyof Event];
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        return 0;
      });
    }
    
    setFilteredEvents(filtered);
  }, [events, searchTerm, sortField, sortOrder, selectedIgreja]);

  async function fetchEvents() {
    try {
      setLoading(true);
      
      // Fetch events
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select('*, event_images(*)');
      
      if (eventsError) throw eventsError;
      
      // Fetch igrejas to get names
      const { data: igrejasData, error: igrejasError } = await supabase
        .from('igrejas')
        .select('id, nome');
      
      if (igrejasError) throw igrejasError;
      
      // Map igreja names to events
      const eventsWithIgrejaNames = eventsData?.map(event => {
        const igreja = igrejasData?.find(i => i.id === event.igreja_id);
        return {
          ...event,
          igreja_nome: igreja?.nome || 'Sem igreja',
          images: event.event_images
        };
      }) || [];
      
      setEvents(eventsWithIgrejaNames);
    } catch (error: any) {
      setError(error.message || 'Erro ao buscar eventos');
    } finally {
      setLoading(false);
    }
  }

  async function fetchIgrejas() {
    try {
      const { data, error } = await supabase
        .from('igrejas')
        .select('id, nome, cidade, estado')
        .order('nome');

      if (error) throw error;
      setIgrejas(data || []);
    } catch (error: any) {
      setError(error.message || 'Erro ao buscar igrejas');
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      const { title, date, time, description, igreja_id, image_url } = formData;
      
      if (editingEvent) {
        // Update existing event
        const { error } = await supabase
          .from('events')
          .update({ title, date, time, description, igreja_id })
          .eq('id', editingEvent.id);
        
        if (error) throw error;
        
        // Update image if provided
        if (image_url) {
          if (editingEvent.images && editingEvent.images.length > 0) {
            // Update existing image
            const { error: imageError } = await supabase
              .from('event_images')
              .update({ image_url })
              .eq('id', editingEvent.images[0].id);
            
            if (imageError) throw imageError;
          } else {
            // Add new image
            const { error: imageError } = await supabase
              .from('event_images')
              .insert([{ event_id: editingEvent.id, image_url }]);
            
            if (imageError) throw imageError;
          }
        }
        
        setSuccess('Evento atualizado com sucesso!');
      } else {
        // Create new event
        const { data, error } = await supabase
          .from('events')
          .insert([{ title, date, time, description, igreja_id }])
          .select();
        
        if (error) throw error;
        
        // Add image if provided
        if (image_url && data && data.length > 0) {
          const { error: imageError } = await supabase
            .from('event_images')
            .insert([{ event_id: data[0].id, image_url }]);
          
          if (imageError) throw imageError;
        }
        
        setSuccess('Evento criado com sucesso!');
      }
      
      // Reset form and close modal
      setFormData({
        title: '',
        date: '',
        time: '',
        description: '',
        igreja_id: '',
        image_url: ''
      });
      setIsModalOpen(false);
      setEditingEvent(null);
      
      // Refresh data
      fetchEvents();
    } catch (error: any) {
      setError(error.message || 'Erro ao salvar evento');
    }
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      date: event.date.split('T')[0], // Format date for input
      time: event.time,
      description: event.description || '',
      igreja_id: event.igreja_id,
      image_url: event.images && event.images.length > 0 ? event.images[0].image_url : ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!eventToDelete) return;
    
    try {
      setError('');
      setSuccess('');
      
      // Delete event
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventToDelete.id);
      
      if (error) throw error;
      
      setSuccess('Evento excluído com sucesso!');
      setIsDeleteModalOpen(false);
      setEventToDelete(null);
      
      // Refresh data
      fetchEvents();
    } catch (error: any) {
      setError(error.message || 'Erro ao excluir evento');
    }
  };

  const handleSortingChange = (field: string, order: 'asc' | 'desc') => {
    setSortField(field);
    setSortOrder(order);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const columns = [
    {
      header: 'Evento',
      accessorKey: 'title',
      enableSorting: true,
      cell: (info: { row: Event }) => (
        <div className="flex items-start gap-3">
          {info.row.images && info.row.images.length > 0 ? (
            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100">
              <img 
                src={info.row.images[0].image_url} 
                alt={info.row.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/100?text=Sem+Imagem';
                }}
              />
            </div>
          ) : (
            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
              <Calendar className="h-6 w-6 text-gray-400" />
            </div>
          )}
          <div>
            <div className="font-medium text-gray-900">{info.row.title}</div>
            {info.row.description && (
              <div className="text-sm text-gray-500 line-clamp-1">{info.row.description}</div>
            )}
          </div>
        </div>
      )
    },
    {
      header: 'Igreja',
      accessorKey: 'igreja_nome',
      enableSorting: true,
    },
    {
      header: 'Data',
      accessorKey: 'date',
      enableSorting: true,
      cell: (info: { row: Event }) => (
        <div>
          <div>{formatDate(info.row.date)}</div>
          <div className="text-sm text-gray-500">{info.row.time}</div>
        </div>
      )
    },
    {
      header: 'Criado em',
      accessorKey: 'created_at',
      enableSorting: true,
      cell: (info: { row: Event }) => (
        <div>{formatDate(info.row.created_at)}</div>
      )
    },
    {
      header: 'Ações',
      accessorKey: 'id',
      cell: (info: { row: Event }) => (
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => handleEdit(info.row)}
            className="p-1 text-brand-teal hover:text-brand-teal/80 rounded-full"
            title="Editar"
          >
            <Edit className="h-5 w-5" />
          </button>
          <button
            onClick={() => {
              setEventToDelete(info.row);
              setIsDeleteModalOpen(true);
            }}
            className="p-1 text-red-500 hover:text-red-600 rounded-full"
            title="Excluir"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  // Calculate pagination
  const pageCount = Math.ceil(filteredEvents.length / pageSize);
  const paginatedData = filteredEvents.slice(
    pageIndex * pageSize,
    (pageIndex + 1) * pageSize
  );

  return (
    <AdminGlobalLayout title="Gerenciar Eventos">
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-lg flex items-start">
          <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      {/* Igreja Filter */}
      <div className="mb-6">
        <label htmlFor="igreja_filter" className="block text-sm font-medium text-gray-700 mb-1">
          Filtrar por Igreja
        </label>
        <select
          id="igreja_filter"
          value={selectedIgreja}
          onChange={(e) => setSelectedIgreja(e.target.value)}
          className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
        >
          <option value="">Todas as Igrejas</option>
          {igrejas.map((igreja) => (
            <option key={igreja.id} value={igreja.id}>
              {igreja.nome} - {igreja.cidade}/{igreja.estado}
            </option>
          ))}
        </select>
      </div>

      <AdminDataTable
        data={paginatedData}
        columns={columns}
        title="Eventos"
        subtitle={`Total de ${filteredEvents.length} eventos cadastrados`}
        loading={loading}
        pagination={{
          pageIndex,
          pageSize,
          pageCount,
          onPageChange: setPageIndex
        }}
        sorting={{
          field: sortField,
          order: sortOrder,
          onSortingChange: handleSortingChange
        }}
        filtering={{
          globalFilter: searchTerm,
          onGlobalFilterChange: setSearchTerm
        }}
        enableExport={true}
        exportFileName="eventos"
        actions={
          <button
            onClick={() => {
              setEditingEvent(null);
              setFormData({
                title: '',
                date: '',
                time: '',
                description: '',
                igreja_id: '',
                image_url: ''
              });
              setIsModalOpen(true);
            }}
            className="px-3 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors flex items-center gap-2 text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>Novo Evento</span>
          </button>
        }
      />

      {/* Event Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-brand-teal mb-6">
              {editingEvent ? 'Editar Evento' : 'Novo Evento'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Título do Evento *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                    Data *
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div>
                  <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                    Horário *
                  </label>
                  <input
                    type="time"
                    id="time"
                    name="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="image_url" className="block text-sm font-medium text-gray-700 mb-1">
                    URL da Imagem
                  </label>
                  <div className="relative">
                    <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="url"
                      id="image_url"
                      name="image_url"
                      value={formData.image_url}
                      onChange={handleInputChange}
                      className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                      placeholder="https://exemplo.com/imagem.jpg"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    URL da imagem de capa do evento. Deixe em branco para manter a imagem atual.
                  </p>
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="igreja_id" className="block text-sm font-medium text-gray-700 mb-1">
                    Igreja *
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <select
                      id="igreja_id"
                      name="igreja_id"
                      value={formData.igreja_id}
                      onChange={handleInputChange}
                      required
                      className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                    >
                      <option value="">Selecione uma igreja...</option>
                      {igrejas.map((igreja) => (
                        <option key={igreja.id} value={igreja.id}>
                          {igreja.nome} - {igreja.cidade}/{igreja.estado}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
                >
                  {editingEvent ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && eventToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmar exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir o evento <strong>{eventToDelete.title}</strong>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setEventToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminGlobalLayout>
  );
}